import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../utils/AuthContext';
import {
  clearStructureCursor,
  isFreshStructureCursor,
  pointerToPageNorm,
  publishStructureCursor,
  STRUCTURE_PRESENCE_THROTTLE_MS,
  subscribeStructurePresence,
  cursorColorForUid,
  type StructureCursorPresence,
} from '../utils/proposalStructurePresenceService';

export function useProposalStructurePresence(dealId: string | undefined, nodeSetId: string) {
  const { user } = useAuth();
  const enabled = !!(dealId && nodeSetId && user?.id);
  const [cursors, setCursors] = useState<StructureCursorPresence[]>([]);
  const [, setTick] = useState(0);
  const lastSentRef = useRef(0);
  const color = useMemo(() => (user?.id ? cursorColorForUid(user.id) : ''), [user?.id]);

  useEffect(() => {
    if (!enabled || !dealId || !user?.id) return undefined;
    return subscribeStructurePresence(dealId, nodeSetId, user.id, setCursors);
  }, [enabled, dealId, nodeSetId, user?.id]);

  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!enabled || !dealId || !user?.id) return undefined;
    return () => {
      void clearStructureCursor(dealId, nodeSetId, user.id);
    };
  }, [enabled, dealId, nodeSetId, user?.id]);

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!enabled || !dealId || !user?.id) return;
      const now = Date.now();
      if (now - lastSentRef.current < STRUCTURE_PRESENCE_THROTTLE_MS) return;
      lastSentRef.current = now;
      const rect = e.currentTarget.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const { x, y } = pointerToPageNorm(e.clientX, e.clientY, rect);
      void publishStructureCursor(dealId, nodeSetId, {
        uid: user.id,
        name: user.name || user.email || 'Someone',
        color,
        x,
        y,
      });
    },
    [enabled, dealId, nodeSetId, user?.id, user?.name, user?.email, color],
  );

  const onPointerLeave = useCallback(() => {
    if (!enabled || !dealId || !user?.id) return;
    void clearStructureCursor(dealId, nodeSetId, user.id);
  }, [enabled, dealId, nodeSetId, user?.id]);

  const fresh = useMemo(() => {
    const now = Date.now();
    return cursors.filter(c => isFreshStructureCursor(c, now));
  }, [cursors]);

  return { enabled, fresh, onPointerMove, onPointerLeave };
}
